import React, { createContext, useCallback, useContext, useEffect, useReducer } from 'react';
import { ProductAttribute, ProductVersion, SaleItemDimension, Unit } from 'models';
// import { SaleItemDimension } from 'models/SaleOrder';
import { Cart, CartAction, CartItem, CartPOSContextType, CartState, CartStatus } from 'types/common';
import { useWarehouseContext } from './warehouseContext';
const CART_STORAGE_KEY_PREFIX = 'liemlan_pos_carts_warehouse_';

// Create context
const CartPOSContext = createContext<CartPOSContextType | null>(null);

// Reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_CART': {
      // Limit to maximum 3 carts
      if (state.carts.length >= 3) return state;
      const newCartId = Date.now().toString();
      const newCart: Cart = {
        id: newCartId,
        name: '#' + (action.payload.name || newCartId.substring(8, 13)),
        status: CartStatus.PRODUCT_SELECTION,
        items: [],
        totalAmount: 0,
      };

      return {
        ...state,
        carts: [...state.carts, newCart],
        activeCartId: newCart.id,
      };
    }

    case 'REMOVE_CART': {
      const updatedCarts = state.carts.filter((cart) => cart.id !== action.payload.id);
      const activeCartId =
        state.activeCartId === action.payload.id
          ? updatedCarts.length > 0
            ? updatedCarts[0].id
            : null
          : state.activeCartId;

      return {
        ...state,
        carts: updatedCarts,
        activeCartId,
      };
    }

    case 'SET_ACTIVE_CART': {
      return {
        ...state,
        activeCartId: action.payload.id,
      };
    }

    case 'UPDATE_CART_STATUS': {
      return {
        ...state,
        carts: state.carts.map((cart) =>
          cart.id === action.payload.id ? { ...cart, status: action.payload.status } : cart,
        ),
      };
    }

    case 'ADD_ITEM': {
      const { cartId, item } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          // Create new item with ID
          const newItem: CartItem = {
            id: crypto.randomUUID(),
            ...item,
            totalPrice: item.quantity * item.unitPrice,
          };

          // Calculate new total
          const newTotal = cart.items.reduce((sum, item) => sum + item.totalPrice, 0) + newItem.totalPrice;

          return {
            ...cart,
            items: [...cart.items, newItem],
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'UPDATE_ITEM': {
      const { cartId, itemId, updates } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const updatedItems = cart.items.map((item) => {
            if (item.id !== itemId) return item;

            // Update item and recalculate total price
            const updatedItem = {
              ...item,
              ...updates,
              totalPrice:
                updates.quantity !== undefined || updates.unitPrice !== undefined
                  ? (updates.quantity !== undefined ? updates.quantity : item.quantity) *
                    (updates.unitPrice !== undefined ? updates.unitPrice : item.unitPrice)
                  : item.totalPrice,
            };

            return updatedItem;
          });

          // Recalculate cart total
          const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

          return {
            ...cart,
            items: updatedItems,
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'REMOVE_ITEM': {
      const { cartId, itemId } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const updatedItems = cart.items.filter((item) => item.id !== itemId);

          // Recalculate cart total
          const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

          return {
            ...cart,
            items: updatedItems,
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'UPDATE_CUSTOMER': {
      const { cartId, customer } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => (cart.id === cartId ? { ...cart, customer } : cart)),
      };
    }

    case 'UPDATE_CART_DISCOUNT': {
      const { cartId, discount } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => (cart.id === cartId ? { ...cart, discount } : cart)),
      };
    }
    case 'UPDATE_CART': {
      const { cartId, updates } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          return {
            ...cart,
            ...updates,
            totalAmount: cart.items.reduce((sum, item) => sum + item.totalPrice, 0) - (updates.discount || 0),
          };
        }),
      };
    }
    case 'CLEAR_CART': {
      const { cartId } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id === cartId) {
            return {
              id: cart.id,
              name: cart.name,
              status: CartStatus.PRODUCT_SELECTION,
              items: [],
              totalAmount: 0,
            };
          }
          return cart;
        }),
      };
    }

    case 'RESET_ALL': {
      return {
        carts: [],
        activeCartId: null,
      };
    }

    case 'SWITCH_WAREHOUSE': {
      // Return the loaded state from new warehouse
      return action.payload.newState;
    }

    default:
      return state;
  }
};
const saveCartsToStorage = (state: CartState, warehouseId: string | undefined) => {
  if (!warehouseId) return;
  try {
    const storageKey = CART_STORAGE_KEY_PREFIX + warehouseId;
    localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save carts to localStorage:', error);
  }
};

const loadCartsFromStorage = (warehouseId: string | undefined): CartState | null => {
  if (!warehouseId) return null;
  try {
    const storageKey = CART_STORAGE_KEY_PREFIX + warehouseId;
    const storedData = localStorage.getItem(storageKey);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (error) {
    console.error('Failed to load carts from localStorage:', error);
    const storageKey = CART_STORAGE_KEY_PREFIX + warehouseId;
    localStorage.removeItem(storageKey);
  }
  return null;
};

// Provider component
export const CartPOSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeWarehouse } = useWarehouseContext();
  const warehouseId = activeWarehouse?.id;

  const getInitialState = (): CartState => {
    const savedState = loadCartsFromStorage(warehouseId);

    if (savedState) {
      return savedState;
    }

    // Default state if nothing in localStorage
    return {
      carts: [
        {
          id: Date.now().toString(),
          name: '#' + Date.now().toString().substring(8, 13),
          status: CartStatus.PRODUCT_SELECTION,
          items: [],
          totalAmount: 0,
        },
      ],
      activeCartId: Date.now().toString(),
    };
  };

  const [state, dispatch] = useReducer((state: CartState, action: CartAction) => {
    const newState = cartReducer(state, action);
    // Save to storage with warehouseId
    saveCartsToStorage(newState, warehouseId);
    return newState;
  }, getInitialState());

  useEffect(() => {
    if (state.carts.length > 0 && !state.activeCartId) {
      dispatch({ type: 'SET_ACTIVE_CART', payload: { id: state.carts[0].id } });
    }
  }, [state.carts, state.activeCartId]);

  // Switch warehouse when warehouseId changes
  useEffect(() => {
    if (!warehouseId) return;

    const newState = loadCartsFromStorage(warehouseId);

    if (newState) {
      // Load existing cart for this warehouse
      dispatch({ type: 'SWITCH_WAREHOUSE', payload: { newState } });
    } else {
      // Create new default cart for this warehouse
      const defaultState: CartState = {
        carts: [
          {
            id: Date.now().toString(),
            name: '#' + Date.now().toString().substring(8, 13),
            status: CartStatus.PRODUCT_SELECTION,
            items: [],
            totalAmount: 0,
          },
        ],
        activeCartId: Date.now().toString(),
      };
      dispatch({ type: 'SWITCH_WAREHOUSE', payload: { newState: defaultState } });
    }
  }, [warehouseId]);

  // Helper function to get the active cart
  const getActiveCart = (): Cart | null => {
    if (!state.activeCartId) return null;
    return state.carts.find((cart) => cart.id === state.activeCartId) || null;
  };

  // Actions
  const addCart = (name: string) => {
    dispatch({ type: 'ADD_CART', payload: { name } });
  };

  const removeCart = (id: string) => {
    dispatch({ type: 'REMOVE_CART', payload: { id } });
  };

  const setActiveCart = (id: string) => {
    dispatch({ type: 'SET_ACTIVE_CART', payload: { id } });
  };

  const updateCartStatus = (id: string, status: CartStatus) => {
    dispatch({ type: 'UPDATE_CART_STATUS', payload: { id, status } });
  };

  const addItem = useCallback(
    (
      productVersion: ProductVersion,
      unit: Unit,
      quantity: number,
      unitPrice?: number,
      attribute?: ProductAttribute,
      cartId?: string,
      saleItemDimensions?: SaleItemDimension[],
      customName?: string,
    ) => {
      const targetCartId = cartId || state.activeCartId;
      if (!targetCartId) return;

      const price = unitPrice !== undefined ? unitPrice : productVersion.salePrice || 0;

      dispatch({
        type: 'ADD_ITEM',
        payload: {
          cartId: targetCartId,
          item: {
            productVersion,
            name:
              customName ||
              [productVersion?.name, productVersion?.color, productVersion?.dimension, productVersion.sizeSpec]
                .filter((product) => product !== undefined && product !== null && product !== '')
                .join(' - ') ||
              '',
            unit,
            quantity,
            unitPrice: price,
            attribute,
            totalPrice: quantity * price,
            saleItemDimesions: saleItemDimensions || [],
          },
        },
      });
    },
    [state.activeCartId, dispatch],
  );

  const updateItem = (itemId: string, updates: Partial<CartItem>) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_ITEM',
      payload: {
        cartId: activeCart.id,
        itemId,
        updates,
      },
    });
  };

  const removeItem = (itemId: string) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'REMOVE_ITEM',
      payload: {
        cartId: activeCart.id,
        itemId,
      },
    });
  };

  const updateCustomer = useCallback(
    (customer: Cart['customer'], cartId?: string) => {
      const targetCartId = cartId || state.activeCartId;
      if (!targetCartId) return;

      dispatch({
        type: 'UPDATE_CUSTOMER',
        payload: {
          cartId: targetCartId,
          customer,
        },
      });
    },
    [state.activeCartId, dispatch],
  );

  const updateDiscount = (discount: number) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_CART_DISCOUNT',
      payload: {
        cartId: activeCart.id,
        discount,
      },
    });
  };
  const updateCart = (cartId: string, updates: Partial<Cart>) => {
    dispatch({
      type: 'UPDATE_CART',
      payload: {
        cartId: cartId,
        updates,
      },
    });
  };
  const clearCart = () => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'CLEAR_CART',
      payload: {
        cartId: activeCart.id,
      },
    });
  };

  const resetAll = () => {
    dispatch({ type: 'RESET_ALL' });
  };

  const switchWarehouse = useCallback((newWarehouseId: string) => {
    const newState = loadCartsFromStorage(newWarehouseId);

    if (newState) {
      dispatch({ type: 'SWITCH_WAREHOUSE', payload: { newState } });
    } else {
      const defaultState: CartState = {
        carts: [
          {
            id: Date.now().toString(),
            name: '#' + Date.now().toString().substring(8, 13),
            status: CartStatus.PRODUCT_SELECTION,
            items: [],
            totalAmount: 0,
          },
        ],
        activeCartId: Date.now().toString(),
      };
      dispatch({ type: 'SWITCH_WAREHOUSE', payload: { newState: defaultState } });
    }
  }, []);

  const contextValue: CartPOSContextType = {
    state,
    addCart,
    removeCart,
    setActiveCart,
    updateCartStatus,
    addItem,
    updateItem,
    removeItem,
    updateCustomer,
    updateDiscount,
    updateCart,
    clearCart,
    resetAll,
    getActiveCart,
    switchWarehouse,
  };

  return <CartPOSContext.Provider value={contextValue}>{children}</CartPOSContext.Provider>;
};
export const clearCartStorage = (warehouseId?: string) => {
  try {
    if (warehouseId) {
      // Clear specific warehouse cart
      const storageKey = CART_STORAGE_KEY_PREFIX + warehouseId;
      localStorage.removeItem(storageKey);
    } else {
      // Clear all warehouse carts
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CART_STORAGE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    console.error('Failed to clear cart storage:', error);
  }
};
// Custom hook for consuming the context
export const useCart = () => {
  const context = useContext(CartPOSContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
